import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GetStatus, removeNonNumeric } from "utils/helpers"
import { FilterCard, FormCard, MainBodyCard, PagesTitle } from "components/Cards";
import SaveButton from "components/Button/SaveButton";
import { useModal } from "context/ModalContext";
import { SystemService } from "services/SystemService/SystemService";
import { ExpenseCategoryService } from "services/ExpenseCategory/ExpenseCategoryService";
import { DropdownStatusList, PageSize } from "utils/Constant";
import Dropdown from "components/Input/Dropdown";
import DataTable from "components/DataTable";
import GridDeleteButton from "components/Button/GridDeleteButton";
import Pagination from "components/Input/PaginationButton";

export default function ExpenseCategory(){
    let navigate = useNavigate()

    const debounceRef = useRef(null)
    const firstLoad = useRef(false)

    const { showFail, showLoading, hideLoading, showSuccess, confirm } = useModal()
    const { fetchSystem, fetchUpdateSystem } = SystemService()
    const { fetchExpenseCategoryList, fetchCreateExpenseCategory, fetchDeleteExpenseCategory } = ExpenseCategoryService()
    
    const [tax, setTax] = useState('')

    const [formExpenseCategoryName, setFormExpenseCategoryName] = useState('')
    const [formDescription, setFormDescription] = useState('')

    const [filterExpenseCategoryName, setFilterExpenseCategoryName] = useState('')
    const [filterStatus, setFilterStatus] = useState(DropdownStatusList[0].value)

    const [expenseCategoryList, setExpenseCategoryList] = useState([])
    const [pageIndex, setPageIndex] = useState(0)
    const [totalPages, setTotalPages] = useState(0)

    const columns = [
        {
            key: "rowNumber",
            header: "No",
            headerClassName: "w-16 font-semibold",
            cellClassName: "text-center",
        },
        {
            key: "expenseCategoryName",
            header: "Expense Category"
        },
        {
            key: "status",
            header: "Status",
            render: (row) => GetStatus(row.status),
        },
        {
            key: "action",
            header: "Action",
            render: (row) => (
                <div className="flex gap-2">
                    <GridDeleteButton isActive={row.status} onClick={() => deleteBtnClick(row.expenseCategoryId, row.status)} size="w-3" />
                </div>                
            ),
        },
    ]

    // Button Event
    const saveBtnClick = async () => {
        const onConfirm = await confirm()
        if (!onConfirm) return

        showLoading()

        try {
            const res = await fetchCreateExpenseCategory(formExpenseCategoryName, formDescription)
            if (!res) return

            showSuccess({
                onSuccess: () => {
                    setFormExpenseCategoryName('')
                    setFormDescription('')

                    handlePageChange(0)
                }
            })
        }
        finally {
            hideLoading()
        }
    }
    const deleteBtnClick = async (id, status) => {
        const isConfirmed = await confirm()

        if (!isConfirmed) return 

        showLoading()
        
        try {
            const res = await fetchDeleteExpenseCategory(id, status)
            if (!res) return
        }
        finally {
            hideLoading()
            showSuccess({
                onSuccess: () => {
                    handlePageChange(pageIndex)
                }
            })
        }
    }

    const handlePageChange = async (newIndex) => {
        const res = await fetchExpenseCategoryList(filterExpenseCategoryName, filterStatus, PageSize, newIndex)
        if (!res) return
        
        const totalData = res?.data?.totalData ?? 0;
        const total = Math.max(1, Math.ceil(totalData / PageSize))

        setExpenseCategoryList(res)
        setTotalPages(total)
        setPageIndex(newIndex)
    }

    useEffect(() => {
        const onLoad = async () => {
            showLoading()

            try {
                if (firstLoad.current) {
                    await handlePageChange(0)

                    firstLoad.current = false
                } 
                else {
                    if (debounceRef.current) {
                        clearTimeout(debounceRef.current)
                    }
            
                    debounceRef.current = setTimeout(async () => {
                        await handlePageChange(0)
                    }, 500)
                }
            }
            finally {
                hideLoading()
            }
        }

        onLoad()
    },[filterExpenseCategoryName, filterStatus])

    return(
        <MainBodyCard>
            <PagesTitle title='Expense Category' />

            <div className="mt-4 border rounded-lg p-3">
                <PagesTitle title='Form' />

                <div className="mt-3 flex flex-col gap-2">
                    <FormCard title='Expense Category Name'>
                        <input className="input" value={formExpenseCategoryName} onChange={(e) => { setFormExpenseCategoryName(e.target.value) }} maxLength={40} />
                    </FormCard>

                    <FormCard title='Description'>
                        <input className="input" value={formDescription} onChange={(e) => { setFormDescription(e.target.value) }} maxLength={40} />
                    </FormCard>

                    <div className="flex justify-end">
                        <SaveButton onClick={saveBtnClick} />
                    </div>
                </div>
            </div>

            <div className="mt-4 border rounded-lg p-3">
                <PagesTitle title='Data' />

                <div className="mt-3 flex gap-3">
                    <FilterCard title='Expense Category Name'>
                        <input className="input" value={filterExpenseCategoryName} onChange={(e) => { setFilterExpenseCategoryName(e.target.value) }} maxLength={40} />
                    </FilterCard>

                    <FilterCard title='Status'>
                        <Dropdown value={filterStatus} options={DropdownStatusList} onChange={(e) => { setFilterStatus(e.value) }} placeholder='All' />
                    </FilterCard>
                </div>

                <div className="mt-5">
                    <DataTable
                        columns={columns}
                        data={expenseCategoryList?.data?.listData ?? []}
                    />
                </div>

                <Pagination
                    pageIndex={pageIndex}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                />
            </div>
        </MainBodyCard>
    )
}